import { useCallback, useEffect, useRef, useState } from 'react';
import type { AsyncState } from '../ui';
import { ApiError, api } from './api';

/**
 * Busca um recurso e devolve o `AsyncState` que o `AsyncView` consome.
 *
 * A razão de existir é fechar o caminho fácil: sem isto, cada tela escreveria
 * seu próprio `useEffect` com `useState` para dados, e — como aconteceu no
 * front anterior — nenhuma delas trataria carregamento nem erro, porque nada
 * obriga. Aqui os estados vêm prontos e a tela precisa lidar com todos.
 *
 * `vazio` decide se a resposta conta como estado vazio. Uma lista vazia é
 * `empty`; um objeto de resumo com zeros não é — ele tem o que mostrar.
 */
export function useResource<T>(
  path: string,
  options: {
    /** Não busca enquanto for falso (permissão, dependência de outra tela). */
    enabled?: boolean;
    /** Motivo exibido quando `enabled` é falso. */
    disabledReason?: string;
    vazio?: (data: T) => boolean;
    /**
     * Teto da consulta no servidor.
     *
     * Vários endpoints truncam em silêncio — `LIMIT 200` nos agendamentos,
     * `LIMIT 100` nos disparos e nas execuções — e devolvem o recorte sem
     * dizer que cortaram. A tela mostrava a lista como se fosse completa: um
     * disparo antigo simplesmente sumia. Quando a resposta vem com o tamanho
     * exato do teto, o estado vira `overflow` e a tela avisa.
     */
    teto?: number;
  } = {},
): { state: AsyncState<T>; recarregar: () => void } {
  const { enabled = true, disabledReason, vazio, teto } = options;
  const [state, setState] = useState<AsyncState<T>>({ status: 'loading' });
  const [tentativa, setTentativa] = useState(0);
  const vazioRef = useRef(vazio);
  vazioRef.current = vazio;

  const recarregar = useCallback(() => setTentativa((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'disabled', reason: disabledReason ?? 'Você não tem acesso a esta seção.' });
      return;
    }

    const controller = new AbortController();
    setState({ status: 'loading' });

    api<T>(path, { signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        const estaVazio = vazioRef.current
          ? vazioRef.current(data)
          : Array.isArray(data) && data.length === 0;
        if (estaVazio) { setState({ status: 'empty' }); return; }
        // `>=` e não `===`: se o teto do servidor mudar para mais, continuar
        // avisando é mais seguro do que voltar a mentir em silêncio.
        if (teto !== undefined && Array.isArray(data) && data.length >= teto) {
          setState({ status: 'overflow', data, shown: data.length, teto });
          return;
        }
        setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        // Aborto não é falha: acontece toda vez que a pessoa troca de tela
        // antes da resposta chegar. Mostrar erro aqui seria mentira.
        if (controller.signal.aborted) return;
        setState({
          status: 'error',
          message: error instanceof ApiError ? error.message : 'Falha de conexão.',
          onRetry: recarregar,
        });
      });

    return () => controller.abort();
  }, [path, enabled, disabledReason, teto, tentativa, recarregar]);

  return { state, recarregar };
}
